import { RichTextEditor } from '@mantine/tiptap';
import { useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { AddCircleOutline, ExpandLess, ExpandMore } from '@mui/icons-material';
import {
    Button,
    Chip,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    IconButton,
    List,
    ListItem,
    Paper,
    Tooltip,
    Typography,
} from '@mui/material';
import React, { Children, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../reducers';
import { currencyFormatter } from '../../../utils/constants';
import SignOffWrapper from '../../SignOffWrapper/SignOffWrapper';
import AddResourceItemModal from '../RequestResolutionModal/AddResourceItemModal';
import ResourceItemRow from '../RequestResolutionModal/ResourceItemRow';

export interface IAddCommentModalProps {
    open: boolean;
    handleClose: () => void;
    handleSave: () => void;
    isAssessment?: boolean;
}

const AddCommentModal: React.FC<IAddCommentModalProps> = ({ open, handleClose, handleSave, isAssessment }) => {
    const {
        selectedRequest: { RequestFor, Id, Zcode, ResourceServiceItems },
        selectedRequest: request,
    } = useSelector((state: RootState) => state.requests);

    const handleSignOffClick = (notes: string): void => {
        handleSave();
    };
    const [summaryNote, setSummaryNote] = useState<string>('');
    const [addResourceModalOpen, setAddResourceModalOpen] = useState<boolean>(false);
    const [collapsedIn, setCollapsedIn] = useState<boolean>(true);

    const summaryNoteEditor = useEditor({
        extensions: [StarterKit],
        content: summaryNote,
        onUpdate: ({ editor }) => {
            setSummaryNote(editor.getHTML());
        },
    });

    useEffect(() => {
        if (summaryNoteEditor && summaryNote !== summaryNoteEditor.getHTML()) {
            summaryNoteEditor.commands.setContent(summaryNote || '');
        }
    }, [summaryNote]);

    const addCommentNote = isAssessment ? (
        <>
            <Typography style={{ textAlign: 'center' }}>Assessment/Request Summary Note</Typography>
            <RichTextEditor editor={summaryNoteEditor}>
                <RichTextEditor.Toolbar>
                    <RichTextEditor.ControlsGroup>
                        <RichTextEditor.Bold />
                        <RichTextEditor.Italic />
                        <RichTextEditor.Underline />
                    </RichTextEditor.ControlsGroup>
                </RichTextEditor.Toolbar>
                <RichTextEditor.Content />
            </RichTextEditor>
        </>
    ) : (
        <SignOffWrapper memberId={RequestFor.Id} requestId={Id} zcode={Zcode} finalizeSignOff={handleSignOffClick} />
    );

    const totalResourceCost = useMemo(
        () => ResourceServiceItems.reduce((a, b) => a + b.PayerList.reduce((a, b) => a + b.Amount, 0), 0),
        [ResourceServiceItems]
    );

    return (
        <Dialog
            open={open}
            fullWidth
            maxWidth={false}
            PaperProps={{
                sx: { alignSelf: 'flex-start' },
            }}
        >
            <DialogTitle>Add Comment</DialogTitle>
            <DialogContent>
                <Grid container spacing={2} style={{ paddingBottom: 5 }}>
                    <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
                        {Id?.length > 0 ? (
                            <AddResourceItemModal open={addResourceModalOpen} onClose={() => setAddResourceModalOpen(false)} request={request} />
                        ) : null}
                        <Divider>
                            <Tooltip title="Click to add resources to the list" arrow>
                                <Chip
                                    label="Resources List"
                                    clickable
                                    color="primary"
                                    icon={<AddCircleOutline />}
                                    onClick={() => setAddResourceModalOpen(true)}
                                />
                            </Tooltip>
                        </Divider>
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
                                <Paper variant="outlined" style={{ padding: 5, marginTop: 10 }}>
                                    {ResourceServiceItems?.length > 0 ? (
                                        <List dense={true}>
                                            <ListItem divider={collapsedIn}>
                                                {collapsedIn ? (
                                                    <IconButton onClick={() => setCollapsedIn(!collapsedIn)}>
                                                        <ExpandMore />
                                                    </IconButton>
                                                ) : (
                                                    <IconButton onClick={() => setCollapsedIn(!collapsedIn)}>
                                                        <ExpandLess />
                                                    </IconButton>
                                                )}
                                                <Grid container justifyContent={'space-evenly'} alignItems="center">
                                                    <Grid>
                                                        <Typography variant="subtitle2">{`Total Contributions Toward Request: ${currencyFormatter.format(
                                                            totalResourceCost
                                                        )}`}</Typography>
                                                    </Grid>
                                                </Grid>
                                            </ListItem>
                                            <Collapse in={collapsedIn}>
                                                <List component={'div'} disablePadding>
                                                    {Children.toArray(
                                                        ResourceServiceItems.map((resource, index) => (
                                                            <ResourceItemRow {...resource} divider={index !== ResourceServiceItems.length - 1} />
                                                        ))
                                                    )}
                                                </List>
                                            </Collapse>
                                        </List>
                                    ) : (
                                        <Typography variant="h6" style={{ textAlign: 'center' }}>
                                            No Resources Added
                                        </Typography>
                                    )}
                                </Paper>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
                {addCommentNote}
            </DialogContent>
            <DialogActions>
                {!isAssessment || summaryNote.replace(/<[^>]*>?/gm, '').trim().length !== 0 ? null : (
                    <Typography variant="caption" color="error" style={{ paddingRight: 20 }}>
                        A note is required before saving.
                    </Typography>
                )}
                <Button variant="contained" onClick={handleClose}>
                    Cancel
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AddCommentModal;
